import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { makeStyles } from 'tss-react/mui'
import type { Theme } from '@mui/material/styles'
import { Paper, Button } from '@mui/material'
import * as yup from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'
import { StackItemTextField } from 'ui/shorthands/StackItemTextField'
import { useForm } from 'react-hook-form'
import type { FieldValues } from 'react-hook-form'
import { Text1, Caption } from 'ui/shorthands/Texts'
import placeholder from 'config/placeholder'
import Stack from 'ui/layout/Stack'
import { ConfirmContent } from './ConfirmContent'
import validationSchema from '../shorthands/validationSchema'
import MarginRightCancelButton from 'ui/shorthands/MarginRightCancelButton'
import { NotFoundError } from 'app/client'
import Modal from 'ui/layout/Modal'

const useStyles = makeStyles()((theme: Theme) => ({
  root: {
    margin: 'auto',
    width: '100%',
  },
  textWrapper: {
    flexWrap: 'wrap',
  },
  text: {
    display: 'inline-flex',
    paddingRight: theme.spacing(2),
  },
  label: {
    wordBreak: 'keep-all',
  },
  formItems: {
    padding: theme.spacing(1, 0),
  },
}))

type ForgotPasswordResultType = 'success' | 'error' | 'not_found'
export type ForgotPasswordErrorProps = { type: ForgotPasswordResultType }

export type ForgotPasswordModalProps = {
  open: boolean
  onSubmit: (data: { email: string }) => Promise<void>
  onClose: () => void
}

const ForgotPasswordModal: React.FC<ForgotPasswordModalProps> = ({
  open,
  onSubmit,
  onClose,
}: ForgotPasswordModalProps) => {
  const { t } = useTranslation()
  const { classes } = useStyles()

  const [forgotPasswordResult, setForgotPasswordResult] = useState<
    ForgotPasswordErrorProps | undefined
  >()

  const [sending, setSending] = useState<boolean>(false)

  const defaultValues: { email: string } = {
    email: '',
  }

  const schema = yup.object().shape({
    email: validationSchema.email(),
  })

  const {
    handleSubmit,
    control,
    reset,
    formState: { isValid, errors },
    setError,
  } = useForm<FieldValues>({
    defaultValues,
    resolver: yupResolver(schema),
    mode: 'onChange',
  })

  const message: {
    [key in ForgotPasswordResultType]: string
  } = {
    success: t('メールをお送りしました'),
    error: t('errors.エラーが発生しました'),
    not_found: t('errors.メールアドレスの登録がありません'),
  }

  const submitForgotPassword = (data: FieldValues) => {
    setSending(true)
    onSubmit(data as { email: string })
      .then(() => setForgotPasswordResult({ type: 'success' }))
      .catch((e) => {
        if (e instanceof NotFoundError) {
          setForgotPasswordResult({ type: 'not_found' })
          return
        }
        setForgotPasswordResult({ type: 'error' })
      })
      .finally(() => {
        window.setTimeout(() => setSending(false), 500)
      })
  }

  const closeHandler = () => {
    onClose()
    //NOTE: confirmが消える時にフォームが表示されるのを防ぐため
    window.setTimeout(() => {
      reset()
      setForgotPasswordResult(undefined)
    }, 1000)
  }

  useEffect(() => {
    if (forgotPasswordResult?.type === 'not_found') {
      setError('email', {
        type: 'not_found',
        message: message.not_found,
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [forgotPasswordResult])

  if (
    forgotPasswordResult &&
    ['success', 'error'].includes(forgotPasswordResult.type)
  ) {
    return (
      <Modal open={open} handleClose={closeHandler}>
        <ConfirmContent
          onSubmit={closeHandler}
          title={message[forgotPasswordResult.type]}
        />
      </Modal>
    )
  }

  return (
    <Modal large open={open} handleClose={closeHandler}>
      <Paper className={classes.root}>
        <form onSubmit={handleSubmit(submitForgotPassword)}>
          <div className={classes.formItems}>
            <Stack className={classes.textWrapper}>
              <div className={classes.text}>
                <Text1>{t('パスワードを忘れた場合')}</Text1>
              </div>
              <div className={classes.text}>
                <Caption>
                  {t('登録済のメールアドレスを入力してください')}
                </Caption>
              </div>
            </Stack>
            <StackItemTextField
              label={<div className={classes.label}>{t('メールアドレス')}</div>}
              name={'email'}
              control={control}
              placeholder={placeholder().email}
              errorMessage={errors.email?.message}
              fullWidth
            />
          </div>
          <Stack align={'right'} borderTop>
            <MarginRightCancelButton onClick={closeHandler} />
            <Button
              color="primary"
              variant="contained"
              type="submit"
              disabled={!isValid || sending}
            >
              {t('送信')}
            </Button>
          </Stack>
        </form>
      </Paper>
    </Modal>
  )
}

export default ForgotPasswordModal
